import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, Users, Pencil, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@shared/utils/cn';
import type { Reservation } from '../hooks/useUserReservations';

interface ReservationCardProps {
  reservation: Reservation;
  onChangeDate: (reservation: Reservation) => void;
  onChangeTime: (reservation: Reservation) => void;
  onChangeSize: (reservation: Reservation) => void;
  onChangeName: (reservation: Reservation) => void;
  onCancel: (reservation: Reservation) => void;
}

/** Parse an ISO datetime string stripping the timezone offset to avoid local conversion. */
function parseWithoutTimezone(isoStr: string): Date {
  const stripped = isoStr.slice(0, 19);
  const [datePart, timePart] = stripped.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  if (!timePart) return new Date(y, m - 1, d, 12, 0, 0);
  const [h, min, s] = timePart.split(':').map(Number);
  return new Date(y, m - 1, d, h, min, s);
}

function formatTime(timeStr: string): string {
  const date = parseWithoutTimezone(timeStr);
  return date.toLocaleTimeString('en', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatDate(dateStr: string): string {
  const date = parseWithoutTimezone(dateStr);
  // "Fri 12 Jun" — compact, scannable, no UPPERCASE shouting.
  const weekday = date.toLocaleDateString('en', { weekday: 'short' });
  const num = date.getDate();
  const month = date.toLocaleDateString('en', { month: 'short' });
  return `${weekday} ${num} ${month}`;
}

function isPastDate(dateStr: string): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parseWithoutTimezone(dateStr) < today;
}

const MODIFIABLE_INTERMEDIARIES = ['', 'MeitrePlatform'] as const;

function canExpand(reservation: Reservation): boolean {
  if (isPastDate(reservation.resDate)) return false;
  const inter = reservation.intermediary ?? '';
  return MODIFIABLE_INTERMEDIARIES.includes(inter as (typeof MODIFIABLE_INTERMEDIARIES)[number]);
}

/**
 * Action row in the expanded state. Subtle surface, brand-color icon as
 * the accent. Matches the §4 rule: brand color is reserved for affordance
 * markers and THE primary action — not for list-row backgrounds. The
 * previous "giant red circle" pattern violated this and read as dated.
 */
function ActionRow({
  icon: Icon,
  label,
  onClick,
}: {
  icon: typeof Calendar;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-foreground/5 hover:bg-foreground/10 transition-colors text-left group"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      <Icon size={18} className="text-brand shrink-0" aria-hidden="true" />
      <span className="text-sm text-foreground flex-1">{label}</span>
      <ChevronRight size={16} className="text-foreground/40 group-hover:text-foreground/70 transition-colors" />
    </button>
  );
}

const ReservationCard = ({
  reservation,
  onChangeDate,
  onChangeTime,
  onChangeSize,
  onChangeName,
  onCancel,
}: ReservationCardProps) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation('profile');
  const expandable = canExpand(reservation);
  const past = isPastDate(reservation.resDate);
  const showActions = !reservation.inWaitingList && !reservation.inRequestBookintList;

  const handleToggle = () => {
    if (expandable) setExpanded((prev) => !prev);
  };

  const subtitleText = reservation.inRequestBookintList
    ? t('EARLY_ACCESS')
    : reservation.inWaitingList
      ? t('WAITING_LIST')
      : reservation.guestName || '';

  // Party-size label. Locale JSON values already carry the correct
  // case per language ("1 person" / "1 Person" — German nouns are
  // always capitalized, so a blanket .toLowerCase() would be a
  // grammatical bug). Rely on the JSON, don't transform at render.
  const partySizeLabel = t('PROFILE_PERSON', { count: reservation.partySize });

  return (
    <div
      className={cn(
        'w-full rounded-xl bg-foreground/5 transition-colors',
        past && 'opacity-60',
      )}
    >
      {/* Collapsed header — single tap target. Focus indicator is a
          subtle inset ring on the OUTER card (not the inner button)
          so the focused state doesn't visually detach the header
          from the expanded content below. */}
      <button
        type="button"
        className={cn(
          'w-full text-left px-5 py-4 rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-card',
          expandable && 'hover:bg-foreground/10 cursor-pointer',
        )}
        onClick={handleToggle}
        disabled={!expandable}
        aria-expanded={expandable ? expanded : undefined}
      >
        <div className="flex items-center gap-4">
          <div className="flex-1 min-w-0">
            {/* Primary: serif date · time */}
            <div className="font-display text-lg text-foreground leading-snug">
              {formatDate(reservation.resDate)} <span className="text-foreground/40">·</span> {formatTime(reservation.resTime)}
            </div>
            {/* Secondary: guest name · party size */}
            <div className="text-sm text-foreground/60 truncate mt-0.5">
              {subtitleText}
              {subtitleText && <span className="text-foreground/30"> · </span>}
              {partySizeLabel}
            </div>
          </div>
          {expandable && (
            <ChevronDown
              size={18}
              className={cn(
                'text-foreground/40 shrink-0 transition-transform',
                expanded && 'rotate-180',
              )}
              aria-hidden="true"
            />
          )}
        </div>
      </button>

      {/* Expanded content — sits inside the same card surface as the
          collapsed header. A subtle hairline divider above the action
          grid + below it visually groups: header → edit actions →
          destructive action. Each region distinct, all on one card. */}
      {expanded && (
        <div className="px-5 pb-5 animate-fade-in">
          {/* Hairline separating "what this reservation is" (header)
              from "what you can do with it" (actions). Pulled to the
              card edges with -mx so it spans the full card. */}
          <div className="-mx-5 border-t border-foreground/10 mb-4" />

          {/* Action grid — two columns ≥ sm, stacked on small viewports */}
          {showActions && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <ActionRow
                icon={Calendar}
                label={t('MODIFY_RESERVATION_STRING_14')}
                onClick={() => onChangeDate(reservation)}
              />
              <ActionRow
                icon={Clock}
                label={t('MODIFY_RESERVATION_STRING_13')}
                onClick={() => onChangeTime(reservation)}
              />
              <ActionRow
                icon={Users}
                label={t('MODIFY_RESERVATION_STRING_12')}
                onClick={() => onChangeSize(reservation)}
              />
              <ActionRow
                icon={Pencil}
                label={t('MODIFY_RESERVATION_STRING_11')}
                onClick={() => onChangeName(reservation)}
              />
            </div>
          )}

          {/* Waiting-list note */}
          {reservation.inWaitingList && (
            <p className="text-center text-sm text-foreground/60 bg-foreground/5 rounded-lg py-2 mt-4">
              {t('WAITING_LIST')}
            </p>
          )}

          {/* Hairline before destructive action — same dividing logic.
              Destructive action lives in its own visual zone, but still
              part of the card. */}
          <div className="-mx-5 border-t border-foreground/10 mt-5 mb-4" />

          {/* Cancel — destructive button matching the action-row register
              (full-width row, same height) but tinted as the alert color.
              The label now explicit ("Cancel reservation", not just
              "Cancel") because in a drawer context bare "Cancel" reads
              as "dismiss this drawer" rather than the actual destructive
              action it triggers. */}
          <button
            type="button"
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-destructive/10 border border-destructive/30 text-destructive text-sm font-medium hover:bg-destructive/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-card"
            onClick={(e) => { e.stopPropagation(); onCancel(reservation); }}
          >
            {t('PROFILE_CANCEL_RESERVATION')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReservationCard;
